import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal hook: adds a "reveal-visible" class once the
 * element enters the viewport, via IntersectionObserver. Falls back to
 * immediately-visible if IntersectionObserver isn't available. The actual
 * fade/translate transition (and the `prefers-reduced-motion` opt-out) is
 * defined once in `landing.scss`, not here, so this hook only toggles state.
 */
export default function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [options]);

  return [ref, visible];
}
